import React, { useCallback, useEffect } from 'react'
import { useRoleById } from '../hooks/useRoleById';
import { useUsers } from '../hooks/useUsers';
import { Button, Modal } from 'react-bootstrap';
import FormSpinner from '../../../shared/components/spinner/FormSpinner';
import FormError from '../../../shared/components/errors/FormError';
import { Controller } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import Select from 'react-select';
import { zodResolver } from '@hookform/resolvers/zod';
import { SyncUsersFormData, SyncUsersSchema } from '../schema/SyncUsersSchema';
import { useAssignUsers } from '../hooks/useAssignUsers';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

interface AssignUserDialogProps {
    show: boolean;
    onClose: () => void;
    roleId: number | undefined;
}

const AssignUserDialog: React.FC<AssignUserDialogProps> = ({
    show,
    onClose,
    roleId
}) => {
    const { data: role, isFetching, isError, isSuccess, error } = useRoleById({
        id: roleId
    });
    const { data: users, isFetching: isUsersFetching, isError: isUsersError, isSuccess: isUsersSuccess, error: usersError } = useUsers({
        id: roleId
    });

    if (isError || isUsersError) {
        console.error(error?.message || usersError?.message || 'Failed to fetch role');
        onClose();
        return;
    }

    const { control, handleSubmit, formState: { errors }, setValue, reset, setError } = useForm<SyncUsersFormData>({
        resolver: zodResolver(SyncUsersSchema),
    });

    useEffect(() => {
        if (role) {
            reset({
                users: role.data.users.map(user => user.id),
            });
        }
    }, [role, reset]);

    const handleClose = () => {
        reset();
        onClose();
    };

    const { mutate, isPending } = useAssignUsers({ roleId });

    const queryClient = useQueryClient();

    const onSubmit = useCallback((values: SyncUsersFormData) => {
        toast.loading('Assign Role...', {
            id: 'assign-role',
        });
        mutate(values, {
            onSuccess: async () => {
                handleClose();

                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['roles'].includes(key as string))
                });

                toast.success('Role assigned successfully', {
                    id: 'assign-role',
                });
            },
            onError: (mutationError) => {
                if ((mutationError as any)?.status === 422) {
                    const validationErrors = (mutationError as any)?.response?.data?.errors;
                    if (validationErrors) {
                        for (const [field, messages] of Object.entries(validationErrors)) {
                            setError(field as keyof SyncUsersFormData, {
                                type: "manual",
                                message: (messages as string[])[0],
                            });
                        }
                        toast.error('Failed to assign role', { id: 'assign-role' });
                    }
                } else {
                    toast.error((mutationError as any)?.response?.data?.message || 'Failed to assign role', { id: 'assign-role' });
                }
            }
        });
    }, [mutate, roleId]);

    const handleSelectChange = (selectedOptions: readonly { value: string, label: string }[] | null) => {
        setValue('users', selectedOptions ? selectedOptions.map(option => option.value) : []);
    };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            backdrop="static"
            keyboard={false}
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title>Assign User</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {isError || isUsersError && <FormError />}
                {isFetching || isUsersFetching && <FormSpinner />}
                {isSuccess && isUsersSuccess && (
                    <form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column">
                        {/* react select multiple user data */}
                        <div className="mb-3">
                            <label htmlFor="users" className="form-label">Users</label>
                            <Controller
                                name="users"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        isMulti
                                        options={users?.data.map(user => ({ value: user.id, label: user.name })) as any}
                                        onChange={(newValue) => handleSelectChange(newValue as readonly { value: string, label: string }[] | null)} // Handle onChange to update the value correctly
                                        className={`${errors.users && 'is-invalid'}`}
                                        placeholder="Select users"
                                        value={Array.isArray(field.value) ? field.value.map((userId: string) => {
                                            const user = users?.data.find(user => user.id === userId);
                                            return user ? { value: user.id, label: user.name } : null;
                                        }) : []} // Ensure field.value is an array before calling map
                                    />
                                )}
                            />
                            {errors.users && <div className="invalid-feedback">{errors.users.message}</div>}
                        </div>
                    </form>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Close
                </Button>
                <Button variant="primary" type='submit' onClick={handleSubmit(onSubmit)} disabled={isPending}>
                    {isPending ? 'Saving...' : 'Save'}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default AssignUserDialog